import React, { useEffect, useRef, useState } from "react";

import { faMobile } from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";

import { invalidateTableView } from "actions";

import AgGridTable from "components/AgGrid/AgGridContainer";
import { MultiButton } from "components/Button";
import { GroupedThirdPartyCard } from "components/BuyerCards/BuyerCards";
import { Row } from "components/Layout";
import { Mobile3x1Layout } from "components/Layout/ContentHeader";
import WaitForSync from "components/LoadingSpinner/WaitForSync";
import ReportsDropdown from "components/ReportsDropdown";
import { SearchHeader } from "components/SearchInput/GlobalSearch";

import { AgGridTables } from "constants/aggrid";
import { ApiModel } from "constants/loading";
import { ParentSaleTabs } from "constants/sale";
import { Settings } from "constants/settings";
import { userTypes } from "constants/users";

import { CurrentAgencySelector } from "containers/ConnectedSelector";

import {
  currentSaleSelector,
  getActiveRole,
  getAgencyById,
  getSaleLotsByBuyer,
  getSaleLotsByBuyerForBusinessUser,
  getSetting,
  getSettings,
  selectAgencyIdByConsignmentIdLookup,
} from "selectors";

import { isLoadingSaleReports } from "selectors/loading";

import { useIsMobile } from "hooks";

import { ColDef } from "ag-grid-community";

import { getSaleReportsColumns } from "./columnDefs";

interface ExtraHeaderComponentProps {
  reportLinks: Array<ReportLink>;
  reportTitle: string;
}

const ExtraHeaderComponent = ({
  reportLinks,
  reportTitle,
}: ExtraHeaderComponentProps) => (
  <Row gridGap={1} fullWidth reverse>
    <div>
      <WaitForSync requiredData={[ApiModel.AGENCIES]}>
        <CurrentAgencySelector includeAll onAfterChanged={undefined} />
      </WaitForSync>
    </div>
    <ReportsDropdown
      options={reportLinks}
      title={reportTitle}
      businessId={undefined}
    />
  </Row>
);
interface SaleReportsTableFilterWrapperProps {
  columnDefinitions: Array<ColDef>;
  date: string | null;
  isLoading?: boolean;
  isBusinessUser?: boolean;
  isCommercialUser?: boolean;
  links: {
    buyer: Array<ReportLink>;
    vendor: Array<ReportLink>;
  };
  reportData: Array<SaleLot>;
  saleyardName: string | null;
}

const SaleReportsTableFilterWrapper = ({
  columnDefinitions = [],
  date = null,
  isLoading = false,
  isBusinessUser = false,
  isCommercialUser = false,
  links = {
    buyer: [],
    vendor: [],
  },
  reportData = [],
  saleyardName = null,
}: SaleReportsTableFilterWrapperProps) => {
  const dispatch = useDispatch();
  const [quickFilterText, setQuickFilterText] = useState("");
  const agencyId =
    useSelector(state => getSettings(state)[Settings.agencyId]) || -1;
  const agency = useSelector(getAgencyById(agencyId));

  const parentSaleTab = useSelector(getSetting(Settings.parentSaleTab));

  const isScreenSizeMobile = useIsMobile();
  const [isMobile, setIsMobile] = useState(isScreenSizeMobile);
  const thirdPartyBuyers = useSelector(
    isBusinessUser ? getSaleLotsByBuyerForBusinessUser : getSaleLotsByBuyer,
  );
  const agencyIdByConsignmentIdLookup = useSelector(
    selectAgencyIdByConsignmentIdLookup,
  );

  // do not show buyer cards to a vendor
  const allowShowBuyerCards =
    !isBusinessUser || parentSaleTab === ParentSaleTabs.BOUGHT;

  const agencyIdRef = useRef(agencyId);

  useEffect(() => {
    if (agencyIdRef.current !== agencyId) {
      dispatch(invalidateTableView(AgGridTables.REPORTS));
      agencyIdRef.current = agencyId;
    }
  }, [agencyId, agencyIdRef, dispatch]);

  const agencyIdByConsignmentIdLookupRef = useRef(
    agencyIdByConsignmentIdLookup,
  );
  agencyIdByConsignmentIdLookupRef.current = agencyIdByConsignmentIdLookup;

  const showCommercialData =
    (isBusinessUser && isCommercialUser) || !isBusinessUser;
  const isReadOnly = isBusinessUser;

  const doesExternalFilterPass = ({ data }) => {
    const consignmentAgencyId =
      agencyIdByConsignmentIdLookupRef.current[data.consignment.id];
    return Boolean(agencyIdRef.current === consignmentAgencyId);
  };

  const isExternalFilterPresent = () => Boolean(agencyIdRef.current > -1);

  useEffect(() => {
    setIsMobile(isScreenSizeMobile);
  }, [setIsMobile, isScreenSizeMobile]);

  const reportLinks = React.useMemo(() => {
    let linksModified = [];

    let relevantTabLinks = [];
    if (parentSaleTab === ParentSaleTabs.BOUGHT) {
      relevantTabLinks = links.buyer;
    } else if (parentSaleTab === ParentSaleTabs.SOLD) {
      relevantTabLinks = links.vendor;
    }

    if (relevantTabLinks.length > 0) {
      const filterByAgent = agencyId !== -1;
      linksModified = relevantTabLinks.reduce(
        // if not consolidated report add agency short name to the end of linkurl
        (
          accum,
          {
            name: linkName,
            url: linkUrl,
            file_type,
            business_name: businessName,
          },
        ) => {
          const url = filterByAgent
            ? `${linkUrl}${
                // determine if there are already query parameters in the link
                linkUrl.includes("?") ? "&" : "?"
              }agency_shortname=${agency?.shortName}`
            : linkUrl;
          accum.push({
            name: filterByAgent
              ? `${agency?.name} - ${linkName} - ${businessName}`
              : `Consolidated ${linkName} - ${businessName}`,
            url,
            file_type,
          });
          return accum;
        },
        [],
      );
    }
    return linksModified;
  }, [agency, agencyId, links, parentSaleTab]);

  const reportTitle = `Download ${
    agencyId > 0 ? agency?.name : "Consolidated"
  } Reports`;

  const extraHeaderComponents = [
    <ReportsDropdown
      options={reportLinks}
      title={reportTitle}
      key="reportLinks"
      businessId={undefined}
    />,
  ];

  const onQuickFilterChange = value => {
    setQuickFilterText(value);
  };
  const additionalActionButtions = [
    allowShowBuyerCards && {
      title: isMobile ? "Desktop View" : "Mobile View",
      icon: faMobile,
      onClick: () => {
        setIsMobile(!isMobile);
      },
      dataTour: "mobileView",
    },
  ];

  if (isMobile && allowShowBuyerCards) {
    if (reportData.length > 0) {
      return (
        <>
          <SearchHeader
            showFacetedSearch={false}
            showBasicSearch
            onBasicSearchTextChanged={onQuickFilterChange}
            actionButton={<MultiButton buttons={additionalActionButtions} />}
            extraHeaderComponents={[
              {
                Component: ExtraHeaderComponent,
                componentProps: {
                  reportLinks,
                  reportTitle,
                },
              },
            ]}
            layout={Mobile3x1Layout}
          />
          {thirdPartyBuyers.map(thirdParty => (
            <GroupedThirdPartyCard
              readOnly={isReadOnly}
              key={`gtpc-${thirdParty.id}`}
              searchText={quickFilterText.toLowerCase()}
              agencyId={agencyId > 0 ? agencyId : null}
              filterValues={{}}
              showCommercialData={showCommercialData}
              thirdParty={thirdParty}
            />
          ))}
        </>
      );
    }
    return <p style={{ textAlign: "center" }}>No rows to show</p>;
  }
  return (
    <AgGridTable
      downloadFilename={`${date}-${saleyardName}-BuyerReport.csv`}
      columnDefs={columnDefinitions}
      rowData={reportData}
      isExternalFilterPresent={isExternalFilterPresent}
      doesExternalFilterPass={doesExternalFilterPass}
      extraActionComponents={extraHeaderComponents}
      defaultView={-1}
      isLoading={isLoading}
      tableName={AgGridTables.REPORTS}
      showGlobalSearchFilter={false}
      showSimpleAgencyFilter
      showTextFilter
      additionalActions={additionalActionButtions}
    />
  );
};

interface SaleReportsTableProps {
  reportData: Array<SaleLot>;
  links: {
    buyer: Array<ReportLink>;
    vendor: Array<ReportLink>;
  };
}

const SaleReportsTable = ({ reportData, links }: SaleReportsTableProps) => {
  const isLoading = useSelector(isLoadingSaleReports);
  const { date, saleyard_name: saleyardName } =
    useSelector(currentSaleSelector);
  const activeRole = useSelector(getActiveRole);

  const isBusinessUser = activeRole.type === userTypes.BUSINESS_USER;

  const isCommercialUser = isBusinessUser && activeRole.is_commercial_user;
  const isComplianceUser = isBusinessUser && activeRole.is_compliance_user;

  const columnDefinitions = getSaleReportsColumns(
    activeRole.type,
    isCommercialUser,
    isComplianceUser,
  );

  return (
    <SaleReportsTableFilterWrapper
      columnDefinitions={columnDefinitions}
      date={date}
      isBusinessUser={isBusinessUser}
      isCommercialUser={isCommercialUser}
      saleyardName={saleyardName}
      reportData={reportData}
      links={links}
      isLoading={isLoading}
    />
  );
};

export default SaleReportsTable;
